import { Quote, QuoteLineItem } from 'core/models/account/amount-to-pay.dto';

export interface HypermediaValueType {
  value: string;
  type: string;
}
export interface HypermediaDisplayValueType {
  value: string;
  displayValue: string;
}
export interface HypermediaSelectFromType {
  value: string;
  selectFrom: HypermediaDisplayValueType[];
}

export interface HypermediaQuoteProperties {
  selectFrom?: HypermediaValueType[];
  accountBalance: HypermediaValueType;
  dateCreated: HypermediaValueType;
  executionDate: HypermediaValueType;
  expirationDate: HypermediaValueType;
  id: HypermediaValueType;
  paymentDueDate: HypermediaValueType;
  quoteAmount: HypermediaValueType;
  quouteFor: HypermediaValueType;
  status: HypermediaValueType;
}

export interface HypermediaQuoteLineItemProperties {
  selectFrom?: HypermediaValueType[];
  agreementId: HypermediaValueType;
  amount: HypermediaValueType;
  id: HypermediaValueType;
  invoiceText: HypermediaValueType;
  isProrated: HypermediaValueType;
  numberOfUnits: HypermediaValueType;
  productId: HypermediaValueType;
  serialNumber: HypermediaValueType;
}

export interface HypermediaLink {
  rel: string[];
  href: string;
}

export interface HypermediaAction {
  class: string[];
  title: string;
  method: string;
  href: string;
  type: string;
}

export interface HypermediaObject {
  class: string[];
  properties?: {
    [key: string]: HypermediaValueType | HypermediaSelectFromType | HypermediaQuoteProperties | HypermediaQuoteLineItemProperties;
  };
  links: HypermediaLink[];
  entities?: HypermediaObject[];
  actions?: HypermediaAction[];
}

export interface HypermediaBeneficiaryPayload {
  DecoderSerialNumber: string;
  BeneficiaryId: string;
}

export class HypermediaUtils {
  static ParseObject(entities: HypermediaObject) {
    if (!Object.prototype.hasOwnProperty?.call(entities, 'properties') || !entities?.properties) {
      return {};
    }
    const rawObject = Object.keys(entities.properties).reduce(
      (previous, key) => ({
        ...previous,
        [key]: Object.prototype.hasOwnProperty?.call(entities.properties?.[key], 'selectFrom')
          ? (entities.properties as { [key: string]: HypermediaSelectFromType })[key]
          : (entities.properties as { [key: string]: HypermediaValueType })[key].value,
      }),
      {}
    );
    return { ...rawObject, actions: entities.actions };
  }

  static ParseObjectToQuote(response: HypermediaObject) {
    const hyperQuoteProperties = response.properties as Partial<HypermediaQuoteProperties>;
    let lineItems: QuoteLineItem[] = [];
    if (response.entities?.[0]?.entities) {
      const lineItemsEntities = response.entities[0].entities;
      lineItems = lineItemsEntities.map((entity) => {
        const rawLineItem = entity.properties as Partial<HypermediaQuoteLineItemProperties>;
        return {
          amount: +(rawLineItem.amount?.value.replace(',', '.') || '0'),
          amountToPay: { value: rawLineItem.amount?.value.replace(',', '.') },
          id: rawLineItem.id?.value,
          invoiceText: rawLineItem.invoiceText?.value,
          endDate: '',
          productName: rawLineItem.productId?.value,
          startDate: '',
        } as QuoteLineItem;
      });
    }

    const newQuote: Quote = {
      accountNumber: hyperQuoteProperties.id?.value || '',
      amountToPay: { value: hyperQuoteProperties.quoteAmount?.value.replace(',', '.') || '' },
      canAccept: response.actions?.find((action) => action.class.includes('accept')) ? true : false,
      canReject: response.actions?.find((action) => action.class.includes('reject')) ? true : false,
      expiryDate: hyperQuoteProperties.expirationDate?.value || '',
      id: hyperQuoteProperties.id?.value || '',
      payByDate: hyperQuoteProperties.paymentDueDate?.value || '',
      quoteDate: hyperQuoteProperties.dateCreated?.value || '',
      type: hyperQuoteProperties.quouteFor?.value || '',
      total: { value: hyperQuoteProperties.accountBalance?.value.replace(',', '.') || '' },
      accountBalance: +(hyperQuoteProperties.accountBalance?.value.replace(',', '.') || ''),
      dateCreated: new Date(hyperQuoteProperties.dateCreated?.value || ''),
      executionDate: new Date(hyperQuoteProperties.executionDate?.value || ''),
      expirationDate: new Date(hyperQuoteProperties.expirationDate?.value || ''),
      payableAmount: +(hyperQuoteProperties.quoteAmount?.value.replace(',', '.') || ''),
      paymentDueDate: new Date(hyperQuoteProperties.paymentDueDate?.value || ''),
      quoteAmount: +(hyperQuoteProperties.quoteAmount?.value.replace(',', '.') || ''),
      quoteFor: hyperQuoteProperties.quouteFor?.value,
      status: hyperQuoteProperties.status?.value,
      lineItems: lineItems,
    };

    return newQuote;
  }

  static ParseResponseToObject<T>(response: HypermediaObject, isParentCollection?: boolean): T {
    if (!response.entities?.length) {
      return HypermediaUtils.ParseObject(response) as T;
    }
    if (response.class[0] === 'quote') {
      return HypermediaUtils.ParseObjectToQuote(response) as T;
    }
    return response.entities?.reduce((prev: any, entity) => {
      let name = entity.class?.filter((c) => c !== 'collection')[0] as string;
      if (name === undefined) name = 'unknown';

      if (!Object.prototype.hasOwnProperty?.call(entity, 'entities')) {
        if ((isParentCollection || response.class.includes('collection')) && Object.prototype.hasOwnProperty?.call(prev, name)) {
          return {
            ...prev,
            [name]: [...prev[name], HypermediaUtils.ParseObject(entity)],
          };
        }
        return {
          ...prev,
          [name]:
            isParentCollection || response.class.includes('collection')
              ? [HypermediaUtils.ParseObject(entity)]
              : HypermediaUtils.ParseObject(entity),
        };
      } else {
        const child = HypermediaUtils.ParseResponseToObject<T>(entity, entity.class.includes('collection'));
        const currentProperties = HypermediaUtils.ParseObject(entity);
        if (Object.prototype.hasOwnProperty?.call(child, name) && Array.isArray(child[name as keyof T])) {
          return {
            ...prev,
            [name]: child[name as keyof T],
          };
        }
        if (Object.prototype.hasOwnProperty?.call(child, name)) {
          return {
            ...prev,
            [name]: { ...currentProperties, ...child[name as keyof T] },
          };
        }
        return {
          ...prev,
          [name]: { ...currentProperties, ...child },
        };
      }
    }, {} as T);
  }

  static ParseHypermediaObject<T>(hypermediaObject: HypermediaObject): T {
    function parseHypermediaObject(obj: HypermediaObject): T {
      const result = {} as T;

      if (obj.properties) {
        for (const key in obj.properties) {
          if (Object.prototype.hasOwnProperty.call(obj.properties, key)) {
            result[key as keyof T] = (obj.properties[key] as HypermediaValueType).value as T[keyof T]; // Use type assertion here
          }
        }
      }
      if (obj.entities) {
        for (const entity of obj.entities) {
          const entityClass = entity.class.filter((c: string) => c !== 'collection')[0];
          const parsedEntity = parseHypermediaObject(entity);

          if (Array.isArray(result[entityClass as keyof T])) {
            (result[entityClass as keyof T] as Array<T>).push(parsedEntity);
          } else if (result[entityClass as keyof T]) {
            result[entityClass as keyof T] = [result[entityClass as keyof T] as T, parsedEntity as T] as T[keyof T];
          } else {
            result[entityClass as keyof T] = parsedEntity as T[keyof T];
          }
        }
      }

      if (obj.actions) {
        (result as { [key: string]: any })['actions'] = obj.actions;
      }

      return result as T;
    }

    return parseHypermediaObject(hypermediaObject);
  }
}